import React, { useMemo } from 'react';
import { Badge } from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { FiEdit3, FiRefreshCw } from 'react-icons/fi';

import { Button } from 'common/materials';
import SupportModal from 'common/components/TopNavigation/SupportModal';

import {
    CreateTicketModal,
    SearchCriteria,
    Tabs,
    TicketCard,
    TicketDetailModal,
    TicketTable
} from '../components';
import useTicketViewModel from './TicketV2.viewmodel';

export const TABS = [
    {
        label: 'All',
        value: 'all',
        color: 'bg-primary-50 text-primary-600'
    },
    {
        label: 'Processing',
        value: 'กำลังดำเนินการ',
        color: 'bg-blue-50 text-blue-600'
    },
    {
        label: 'Cancel',
        value: 'ถูกยกเลิก',
        color: 'bg-red-50 text-red-600'
    },
    {
        label: 'Finish',
        value: 'ปิดงานแล้ว',
        color: 'bg-green-50 text-green-600'
    }
] as const;

function TicketV2View() {
    const {
        email,
        linkId,
        filter,
        dateRange,
        displayStyle,
        tasks = [],
        isLoadingMore,
        isRefreshingTaskInbox,
        filteredTasks,
        search,
        showSupportModal,
        showCreateTicketModal,
        selectedTask,
        selectedTaskDetail,
        showTaskDetailModal,
        setFilter,
        setDateRange,
        setSearch,
        setDisplayStyle,
        handleClickTicketDetail,
        handleCloseTicketDetailModal,
        handleClickCloseTask,
        handleClickCancelTask,
        handleUpdateTaskProgress,
        handleSubmitComment,
        setShowSupportModal,
        setShowCreateTicketModal,
        handleOpenSupportModal,
        handleOpenCreateTicketModal,
        refreshTaskInbox,
        onReplyEmail
    } = useTicketViewModel();

    const statusCounts = useMemo(
        () =>
            tasks.reduce((accumulate, task) => {
                return accumulate.set(
                    task.activityOverviewStatus,
                    (accumulate.get(task.activityOverviewStatus) ?? 0) + 1
                );
            }, new Map<string, number>()),
        [tasks]
    );

    const tabs = useMemo(
        () =>
            [
                {
                    label: `All (${tasks.length})`,
                    value: 'all'
                },
                {
                    label: `Processing (${statusCounts.get('กำลังดำเนินการ') ?? 0})`,
                    value: 'กำลังดำเนินการ'
                },
                {
                    label: `Cancel (${statusCounts.get('ถูกยกเลิก') ?? 0})`,
                    value: 'ถูกยกเลิก'
                },
                {
                    label: `Finish (${statusCounts.get('ปิดงานแล้ว') ?? 0})`,
                    value: 'ปิดงานแล้ว'
                }
            ] as const,
        [tasks, statusCounts]
    );

    return (
        <React.Fragment>
            <div className="flex flex-1 flex-col gap-4">
                <div className="flex">
                    <p className="text-2xl font-bold">TASK TICKET</p>
                    <div className="ml-auto flex gap-2">
                        <Button
                            outline
                            className="flex flex-row items-center gap-2 rounded-[6px] px-4"
                            onClick={handleOpenSupportModal}
                        >
                            <FaPlus size={10} />
                            <span className="text-sm">Report Application Problem to F1</span>
                        </Button>
                        <Button
                            className="flex flex-row items-center gap-2 rounded-[6px] bg-primary-900 px-4 text-white"
                            onClick={handleOpenCreateTicketModal}
                        >
                            <FiEdit3 size={12} />
                            <span className="text-sm">Internal Work Assignment</span>
                        </Button>
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <Badge
                        variant="subtle"
                        colorScheme="primary"
                        className="w-fit rounded-md px-2 py-1 text-xs"
                    >
                        {email}
                    </Badge>
                    <div className="flex items-center gap-3">
                        {isLoadingMore && (
                            <span className="text-xs text-gray-500">
                                กำลังโหลดรายการเพิ่มเติม...
                            </span>
                        )}
                        <Button
                            outline
                            disabled={!linkId || isRefreshingTaskInbox}
                            onClick={() => void refreshTaskInbox()}
                            className="flex items-center gap-2 rounded-[6px] px-3"
                        >
                            <FiRefreshCw
                                size={14}
                                className={isRefreshingTaskInbox ? 'animate-spin' : ''}
                            />
                            <span className="text-sm">
                                {isRefreshingTaskInbox ? 'กำลังรีเฟรช...' : 'รีเฟรชรายการ'}
                            </span>
                        </Button>
                    </div>
                </div>

                <Tabs
                    tabs={tabs}
                    value={filter}
                    onClick={setFilter}
                />

                <SearchCriteria
                    dateRange={dateRange}
                    keyword={search}
                    viewMode={displayStyle}
                    onDateRangeChange={setDateRange}
                    onKeywordChange={setSearch}
                    onViewModeChange={setDisplayStyle}
                    className="border-b pb-5"
                />

                {displayStyle === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 overflow-hidden sm:grid-cols-2 2xl:grid-cols-3">
                        {filteredTasks && filteredTasks.length > 0 ? (
                            filteredTasks.map((ticket, index) => (
                                <TicketCard
                                    key={index}
                                    ticket={ticket}
                                    onClickTicketDetail={handleClickTicketDetail}
                                />
                            ))
                        ) : (
                            <div className="col-span-2 text-center">No ticket...</div>
                        )}
                    </div>
                ) : (
                    <TicketTable
                        tasks={filteredTasks}
                        onClickTicketDetail={handleClickTicketDetail}
                    />
                )}
            </div>

            {/* Create ticket modal */}
            <SupportModal
                header="Internal Work Assignment"
                isOpen={showSupportModal}
                onClose={setShowSupportModal.off}
            />

            <CreateTicketModal
                isOpen={showCreateTicketModal}
                onClose={setShowCreateTicketModal.off}
                onCreated={refreshTaskInbox}
            />

            <TicketDetailModal
                isOpen={showTaskDetailModal}
                task={selectedTask}
                taskActivity={selectedTaskDetail}
                currentLinkId={linkId}
                onClose={handleCloseTicketDetailModal}
                onReplyEmail={onReplyEmail}
                onSubmitComment={handleSubmitComment}
                onClickCloseTask={handleClickCloseTask}
                onClickCancelTask={handleClickCancelTask}
                onUpdateProgress={handleUpdateTaskProgress}
            />
        </React.Fragment>
    );
}

export default TicketV2View;
